import "./listPurchases.scss";

import { ChevronsDown, ChevronDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { useEffect, useState } from "react";
import { fetchPurchases, setLoadingResults, setModalAddPurchaseStatus, updatePurchase } from "../../../store/PurchaseSlice";
import { fetchSupplier } from "../../../store/SuppliersSlice";
import ModalAddPurchase from "../../../components/modal/modal-add-purchase/modal-add-purchase";
import CancelPurchaseModal from "../../../components/modal/modal-cancel-purchase/modal-cancel-purchase";
import { fetchClientsByOrganization } from "../../../store/UserSlice";
import { IPurchase, PurchaseStatus, PURCHASE_EDITABLE_STATUS_OPTIONS, purchaseStatusLabel } from "../../../models/Purchase";
import { useRole } from "../../../auth/useRole";
import {
    fomartAmountBought, formatAmountPaidToTarget,
    formatPercentage, formatProfit, profitValue,
} from "../../../utils/purchase-utils";
import { formatCurrencyBRL } from "../../../utils/format";

import { DataList, DataListColumn, MoneyValue, StatusBadge } from "../../../components/ui";

const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

const ListPurchases = () => {

    const statusOptions = PURCHASE_EDITABLE_STATUS_OPTIONS;

    // Status da compra: so o owner altera (o backend tambem bloqueia), e
    // compra cancelada nao muda mais.
    const { role } = useRole();
    const canEditStatus = role === "owner";

    // Compra aguardando confirmacao de cancelamento no modal.
    const [cancelId, setCancelId] = useState<string | null>(null);

    const dispatch = useAppDispatch();
    const modalStatus                              = useAppSelector((s) => s.purchase.modalAddPurchaseStatus);
    const purchasesList: Array<IPurchase> | null   = useAppSelector((s) => s.purchase.purchasesList);
    const loadingResults: boolean                  = useAppSelector((s) => s.purchase.loadingResults);

    useEffect(() => {
        dispatch(fetchPurchases());
        dispatch(fetchSupplier());
        dispatch(fetchClientsByOrganization());
    }, []);

    const toggle = () => dispatch(setModalAddPurchaseStatus(!modalStatus));

    const handleStatusChange = (p: IPurchase, newStatus: string) => {
        if (newStatus === p.status) return;
        // Cancelar estorna os movimentos: passa pelo modal de confirmacao.
        if (newStatus === "CANCELED") {
            setCancelId(p.id);
            return;
        }
        const updated = { ...p, status: newStatus as PurchaseStatus };
        dispatch(setLoadingResults(!loadingResults));
        dispatch(updatePurchase(updated));
    };

    const columns: DataListColumn<IPurchase>[] = [
        {
            key: "supplier",
            header: "Fornecedor",
            width: "1.1fr",
            render: (p) => (
                <span className="cell-stack">
                    <strong>{p?.target_user?.name ?? "—"}</strong>
                    <small style={{ opacity: .55 }}>{p?.target_user?.email ?? ""}</small>
                </span>
            ),
        },
        {
            key: "client",
            header: "Cliente",
            width: "1.1fr",
            render: (p) => (
                <span className="cell-stack">
                    <strong>{p?.origin_user?.name ?? "—"}</strong>
                    <small style={{ opacity: .55 }}>{p?.origin_user?.email ?? ""}</small>
                </span>
            ),
        },
        {
            key: "amount",
            header: "Quantidade",
            width: "1.3fr",
            render: (p) => (
                <span className="cell-stack">
                    <strong className="num">
                        {fomartAmountBought(p)}{" "}
                        <span style={{ opacity: .65, fontSize: ".72rem" }}>{p.assetTarget?.symbol ?? ""}</span>
                    </strong>
                    <small className="num" style={{ opacity: .5 }}>
                        {p?.trx_id.slice(0,10) + "..." ?? ""}
                    </small>
                </span>
            ),
        },
        {
            key: "valuePaid",
            header: (
                <>
                    Total pago
                    <br />
                    <small>(BRL)</small>
                </>
            ),
            width: "1.1fr",
            align: "right",
            render: (p) => {
                const total = toNum(p.value);
                return (
                    <span className="cell-stack">
                        <strong className="num" style={{ opacity: .75 }}>
                            {total !== null ? <MoneyValue amount={total} tone="neutral" /> : "—"}
                        </strong>
                        <small>
                            -
                        </small>
                    </span>
                );
            },
        },
        {
            key: "sale",
            header: (
                <>
                    Venda
                    <br />
                    <small>(cotação / total)</small>
                </>
            ),
            width: "1.2fr",
            align: "right",
            render: (p) => {
                const cot   = toNum(p.quotation_organization);
                const total = toNum(p.value);
                return (
                    <span className="cell-stack right">
                        <strong className="num">
                            {cot !== null ? <MoneyValue amount={cot} tone="neutral" /> : "—"}
                        </strong>
                        <small className="num" style={{ opacity: .55 }}>
                            total {total !== null ? <MoneyValue amount={total} tone="neutral" /> : "—"}
                        </small>
                    </span>
                );
            },
        },
        {
            key: "buy",
            header: (
                <>
                    Compra
                    <br />
                    <small>(cotação / total)</small>
                </>
            ),
            width: "1.2fr",
            align: "right",
            render: (p) => {
                const cot = toNum(p.quotation_real);
                return (
                    <span className="cell-stack right">
                        <strong className="num">
                            {cot !== null ? <MoneyValue amount={cot} tone="neutral" /> : "—"}
                        </strong>
                        <small className="num" style={{ opacity: .55 }}>
                            total {formatAmountPaidToTarget(p)}
                        </small>
                    </span>
                );
            },
        },
        {
            key: "invoice",
            header: "Repasse ",
            width: "1.2fr",
            align: "right",
            render: (p) => {
                const cot = toNum(p.quotation_real);
                return (
                    <span className="cell-stack right">
                        <strong className="num">
                            {formatAmountPaidToTarget(p)}
                        </strong>
                    </span>
                );
            },
        },        
        {
            key: "profit",
            header: "Saldo",
            width: "1fr",
            align: "right",
            render: (p) => {
                // Prefere o saldo persistido (purchases.saldo); compras sem
                // ele (cotacoes zeradas) caem no calculo local legado.
                const value = p.saldo != null ? Number(p.saldo) : profitValue(p);
                const klass = value === null
                    ? ""
                    : value > 0 ? "delta-up"
                    : value < 0 ? "delta-down"
                    : "";
                return (
                    <span className="cell-stack right">
                        <strong className={`num ${klass}`}>
                            {p.saldo != null ? formatCurrencyBRL(Number(p.saldo)) : formatProfit(p)}
                        </strong>
                        <small className={`num ${klass}`} style={{ opacity: .7 }}>
                            {formatPercentage(p)}
                        </small>
                    </span>
                );
            },
        },
        {
            key: "date",
            header: "Data",
            width: "140px",
            render: (p) => {
                const d = p.updatedAt ? new Date(p.updatedAt) : null;
                const ok = d && !Number.isNaN(d.getTime());

                return (
                    <span className="num" style={{ opacity: .65, fontSize: ".75rem" }}>
                        {ok ? (
                            <>
                                {d.toLocaleDateString("pt-BR")}
                                <br />
                                {d.toLocaleTimeString("pt-BR")}
                            </>
                        ) : "—"}
                    </span>
                );
            },
        },
        {
            key: "status",
            header: "Status",
            width: "150px",
            render: (p) => (!canEditStatus || p.status === "CANCELED") ? (
                <StatusBadge status={purchaseStatusLabel(p.status)} />
            ) : (
                <div className="status-cell" onClick={(e) => e.stopPropagation()}>
                    <StatusBadge status={purchaseStatusLabel(p.status)} />
                    <ChevronDown size={12} className="status-chevron" />
                    <select
                        className="status-edit"
                        value={p.status ?? ""}
                        onChange={(e) => handleStatusChange(p, e.target.value)}
                        aria-label="Alterar status"
                    >
                        {p.status && !statusOptions.some((s) => s.value === p.status) && (
                            <option value={p.status} hidden>{purchaseStatusLabel(p.status)}</option>
                        )}
                        {statusOptions.map((s) => (
                            <option value={s.value} key={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="admin-client-purchases-wrapper">

                <div className="flex sb">
                    <h1 className="page-title">
                        Swaps
                        <small>lista de todos os swaps realizados pelos clientes na plataforma.</small>
                    </h1>
                    <div className="buttons">
                        <button className="btn-pill primary" onClick={toggle}>Adicionar Swap</button>
                    </div>
                </div>

                <DataList
                    columns={columns}
                    rows={purchasesList ?? []}
                    loading={!purchasesList || loadingResults}
                    empty="Sem compras feitas no momento."
                />

                <div className="buttons load-more">
                    <button className="btn-pill ghost">
                        <ChevronsDown size={16} /> carregar mais <ChevronsDown size={16} />
                    </button>
                </div>

            </div>

            {modalStatus && <ModalAddPurchase />}

            {cancelId && (
                <CancelPurchaseModal
                    purchaseId={cancelId}
                    onClose={() => setCancelId(null)}
                    onCanceled={() => dispatch(fetchPurchases())}
                />
            )}
        </>
    );
};

export default ListPurchases;
